/*
 * The viewer's home card, branded for StackSchematic.
 *
 * The scaffolded default welcomed people to "Backstage" and linked its generic concepts
 * (Software Catalog, Software Template, backstage.io docs) — stock boilerplate on a site whose
 * whole point is a different product. This replaces it: it welcomes people to StackSchematic and
 * orients them by the product family and the evidence model, using only routes the overlay
 * actually installs.
 *
 * Guide is reached through the floating dock (bottom-right) rather than a link here. The rest of
 * the family is in the sidebar as first-class items (there is no "Apps" bucket); this card links
 * the ones worth starting with, by the routes the overlay actually installs.
 *
 * apply.mjs copies this over the scaffolded modules/home/homeModule.tsx; App.tsx already imports
 * `homeModule` from there, so the export name is load-bearing and must not change.
 */
import { createFrontendModule } from '@backstage/frontend-plugin-api';
import { HomePageWidgetBlueprint } from '@backstage/plugin-home-react/alpha';
import { MarkdownContent } from '@backstage/core-components';

const content = `
## Welcome to StackSchematic 👋

An application-centric, continuously updated blueprint of your technology estate,
organized the way the business thinks about it, not the way infrastructure tooling
happens to emit it. **Every fact carries where it came from and how confident you
can be in it.**

### Start with the blueprint

- **[Blueprint](/blueprint)**: the estate by application, each dependency shown with
  the evidence it rests on.
- **[Catalog](/catalog)**: every entity anybody has declared or discovery has found. A
  declared one carries who said so, when, and on what evidence, as annotations.
- **[Graph](/catalog-graph)**: the relationships between everything, drawn. What
  StackSchematic only guessed is marked on the thing it qualifies, in Discovery.
- **[Agents](/agents)**: the discovery fleet, and what each vantage point has seen.

### Follow the evidence

- **[Discovery](/discovery)**: the estate the fleet found by scanning, and what has —
  or has not — been witnessed using any of it.
- **[Connect](/connect)**: which evidence sources are delivering, and which cannot look.
- **[Docs](/docs)**: the architecture, the decisions, and the runbooks.

Every relationship is \`DECLARED\`, \`DISCOVERED\`, \`OBSERVED\`, \`PROBED\` or
\`INFERRED\`. Nothing is presented as truth because a heuristic produced it, and a guess
is never laundered into a fact. Every product is in the sidebar; ask **Guide**
(bottom-right) anything about the blueprint: it reads everything and asserts nothing.
`;

const gettingStartedWidget = HomePageWidgetBlueprint.make({
  name: 'getting-started',
  params: {
    name: 'GettingStarted',
    title: 'Getting started',
    description: 'Where to start in your technology blueprint',
    // Sizing hints, used ONLY as the fallback when the home page has no defaultConfig. On this
    // deployment the layout is pinned by `page:home` in app-config.stackschematic.yaml (a
    // defaultConfig overrides these hints), which is where the card's real height lives; this
    // keeps the card readable if that config is ever removed. See that file's page:home comment.
    layout: {
      height: { minRows: 10, defaultRows: 12 },
    },
    components: async () => ({
      Content: () => <MarkdownContent content={content} />,
    }),
  },
});

export const homeModule = createFrontendModule({
  pluginId: 'home',
  extensions: [gettingStartedWidget],
});
